import { useContext, useEffect, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

//Variables / Types
import { useQuery } from '@apollo/client';
import { A1_POWEREDBY_COLOR_PRIMARY, COMMUNITY_URL } from '../config';
import { EventObject, OrderSummaryEventDocument, OrderSummaryEventQuery } from '../graphql/graphql';
import { CustomOrderSummaryPageProps } from '../types/common';

// MUI / Components
import { Container } from '@mui/material';
import { ThemeProvider } from '../ThemeProvider';
import HeaderJoyComponent from '../components/common/HeaderJoy.component';
import OrderSummaryLoadedJoy from '../components/orderSummary/orderSummaryLoadedJoy.component.tsx';
import OrderSummaryLoading from '../components/orderSummary/orderSummaryLoading.component';
import { CartContext } from '../components/providers/cart.tsx';
import useEventPixel from '../hooks/useEventPixel.tsx';
import { formatUrl } from '../utils/formatUrl.ts';
import { formatPrimaryColor } from '../utils/formatter.ts';
import { isReallyEmpty } from '../utils/validators.ts';
import { useTranslation } from 'react-i18next';

export default function OrderSummaryPage({ match }: CustomOrderSummaryPageProps) {
    const { t } = useTranslation();
    const { addUnlockCode, addReferralCode, sanitizeCart } = useContext(CartContext);

    const [errorAlert, setErrorAlert] = useState<string | null>(null);

    const history = useHistory();
    const location = useLocation();
    const eventId = match.params.id;

    const { trackEvent, initialized } = useEventPixel(eventId);

    const { loading, data, error } = useQuery<OrderSummaryEventQuery>(OrderSummaryEventDocument, {
        variables: {
            eventId,
        },
        fetchPolicy: 'network-only',
        onCompleted: async (data) => {
            if (!data?.event) {
                return;
            }

            const query = new URLSearchParams(location.search);

            if (data.event.fbpid) {
                ReactPixel.init(data.event.fbpid);
                ReactPixel.pageView();
            }

            const reset = query.get('reset');
            sanitizeCart(data.event, Boolean(reset));

            const referralQuery = query.get('referral');

            if (referralQuery && referralQuery?.length > 0) {
                addReferralCode(referralQuery);
            }

            const unlockCodes = query.get('unlockcodes');

            if (unlockCodes) {
                const unlockCodesArray = unlockCodes
                    .split(',')
                    .map((c) => c.toUpperCase().trim())
                    .filter(String);

                if (unlockCodesArray?.length > 0) {
                    addUnlockCode(...unlockCodesArray);
                }
            }

            query.delete('reset');
            query.delete('unlockcodes');

            history.replace({
                ...location,
                search: query.toString() === '' ? '' : `?${query.toString()}`,
            });
        },
    });

    // Track ViewContent event when the component mounts and pixel is initialized
    useEffect(() => {
        if (initialized && data?.event) {
            // Track a standard event
            trackEvent('ViewContent', {
                content_type: 'event',
                content_name: data.event.title || t('orderSummaryPage.contentNameFallback', 'Event'),
                content_ids: [eventId],
            });
        }
    }, [initialized, data, eventId, trackEvent]);

    const organizerThemeColor = data?.event?.organizerColor || A1_POWEREDBY_COLOR_PRIMARY;

    const organizerPrimaryColor = formatPrimaryColor(
        data?.event?.eventType,
        data?.event?.organizerType,
        organizerThemeColor
    );

    const organizerSecondaryColor =
        data?.event?.organizerType === 'poweredby' ? organizerThemeColor : '#01680B';

    if (!loading && !data?.event) {
        return <Redirect to="/404" />;
    }

    if (!loading && error) {
        return <>{t('orderSummaryPage.errorLoadingData', 'Error Loading Data')}</>;
    }

    const hasAdditionalTerms =
        data?.event?.additionalTerms != null && !isReallyEmpty(data?.event?.additionalTerms);

    const handleContinueClick = () => {
        if (initialized) {
            trackEvent('InitiateCheckout', {
                content_type: 'event_ticket',
                content_ids: [eventId],
                content_name: data?.event?.title || t('orderSummaryPage.eventFallback', 'Event'),
            });
        }
    };

    const hasQuestionnaire =
        (data?.event?.questionnaire && data?.event?.questionnaire?.questions.length > 0) || false;

    return (
        <ThemeProvider>
            <>
                <Container sx={{ py: 6 }}>
                    {loading ? (
                        <>
                            <OrderSummaryLoading />
                        </>
                    ) : (
                        <>
                            <HeaderJoyComponent
                                hasAdditionalTerms={hasAdditionalTerms}
                                previousPage={`${COMMUNITY_URL}${formatUrl(
                                    data?.event as EventObject
                                )}`}
                                step={1}
                                organizerPrimaryColor={organizerPrimaryColor}
                                organizerSecondaryColor={organizerSecondaryColor}
                                headerTitle={t('orderSummaryPage.title', 'Order Summary')}
                                setErrorAlert={setErrorAlert}
                                errorAlert={errorAlert}
                                hasQuestionnaire={hasQuestionnaire}
                            />
                            <OrderSummaryLoadedJoy
                                hasAdditionalTerms={hasAdditionalTerms}
                                event={data?.event}
                                organizerPrimaryColor={organizerPrimaryColor}
                                organizerSecondaryColor={organizerSecondaryColor}
                                setErrorAlert={setErrorAlert}
                                onContinueClick={handleContinueClick}
                                hasQuestionnaire={hasQuestionnaire}
                            />
                        </>
                    )}
                </Container>
            </>
        </ThemeProvider>
    );
}
